import React, {useEffect, useMemo, useState} from "react";
import {Box, Text, useMediaQuery} from "@chakra-ui/react";
import { useDrop } from "react-dnd";
import { ItemTypes } from "./DraggableNumberBox";
import {DigitalNumberBoxSize, OperatorModel, ValidationStatusModel} from "../types";
import {
  firstOperandPrefix, regroupingOperandPrefix, secondOperandPrefix
} from "../pages/MainScreen/components/ExerciseScreen";

interface DroppableNumberBoxProps {
  name: string;
  label: string;
  focused?: boolean;
  onClick?: () => void;
  validationStatus?: ValidationStatusModel;
  regroupingMode?: boolean;
  operator?: OperatorModel;
  size?: DigitalNumberBoxSize;
  width?: number;
  disableMargin?: boolean;
  onBoxHeightChange?: (height: number) => void;
  crossed?: boolean;
  extraNumber?: string;
}

export default function DroppableNumberBox({
  name,
  label,
  onClick,
  focused,
  validationStatus,
  regroupingMode,
  operator,
  size,
  width,
  disableMargin,
  onBoxHeightChange,
  crossed,
  extraNumber
}: DroppableNumberBoxProps) {
  const [{ canDrop, isOver }, drop] = useDrop(
    () => ({
      accept: ItemTypes.BOX,
      drop: (item) => {
        return {
          name: name,
          allowedDropEffect: "copy",
        };
      },
      collect: (monitor: any) => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    []
  );
  const [isLargeScreen] = useMediaQuery("(min-width: 764px)");

  const selectBackgroundColor = (
    isActive: boolean,
    canDrop: boolean,
    name: string,
    validationStatus?: ValidationStatusModel,
    regrouping?: boolean
  ) => {
    const regroupingOperandBox = name.includes(regroupingOperandPrefix);
    const firstOperandBox = name.includes(firstOperandPrefix);
    const secondOperandBox = name.includes(secondOperandPrefix);
    const answerOperandBox = !firstOperandBox && !secondOperandBox && !regroupingOperandBox;
    if (validationStatus) {
      return validationStatus === "success" ? "#BEFCBD" : "#FBC8AB";
    }
    if (isActive) {
      return "darkgreen";
    } else if (canDrop) {
      return (operator === OperatorModel.SUBSTRACTION ?
        ((regrouping && !answerOperandBox) || (!regrouping && !regroupingOperandBox)) :
        ((!regrouping && !regroupingOperandBox) || (regrouping && regroupingOperandBox))
      ) ?
        "darkkhaki" : regroupingOperandBox ? "#94DBF2" : "transparent";
    } else {
      return regroupingOperandBox ? "#94DBF2" : "transparent";
    }
  }

  const isActive = canDrop && isOver;
  const backgroundColor = selectBackgroundColor(
    isActive,
    canDrop,
    name,
    validationStatus,
    regroupingMode
  );

  const numberBoxSize = useMemo(
    () => (size === "lg" ? 50 : size === "md" ? 32 : 24),
    [size]
  );

  const [boxHeight, setBoxHeight] = useState(numberBoxSize);
  useEffect(() => {onBoxHeightChange && onBoxHeightChange(boxHeight)}, [boxHeight]);

  const regroupingNumberStyles = {
    position: "absolute",
    background: "#94DBF2",
    border: "0.5px solid #FFD",
    borderRadius: "4px",
    px: "4px",
    textAlign: "center",
    fontSize: "10px",
    w: "16px",
    h: "16px",
    fontWeight: "700"
  }

  const regroupingCrossBoxStyles = {
    position: "absolute",
    background: "#307ACF",
    width: "20px",
    height: "4px",
    px: "4px",
    transform: "rotate(-45deg)"
  }

  return (
    <Box
      ref={drop}
      onClick={onClick}
      sx={{
        width: width ? `${width}px` : size === "sm" ? `100%` : `${numberBoxSize}px`,
        height: size === "sm" ? `${boxHeight}px` : `${numberBoxSize}px`,
        border: name.includes(regroupingOperandPrefix) ? "none" : "1px solid #EEF05D",
        maxWidth: size === "sm" ? "40px" : "auto",
        borderRadius: size === "lg" ? "12px" : "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgColor: "#fff",
        fontWeight: 900,
        position: "relative",
        boxShadow: focused ? "var(--chakra-shadows-outline)" : "none",
        ml: disableMargin ? 0 : isLargeScreen ? "10px" : "8px",
        fontSize: isLargeScreen ? "1.26rem" : "1rem",
        backgroundColor,
      }}
    >
      <div style={{position: "absolute", width: "calc(100% + 2px)"}} ref={ref => ref && setBoxHeight(ref.offsetWidth)}/>
      {label === "↑" ? "+1" : label}
      {crossed && <Box sx={{...regroupingCrossBoxStyles}}/>}
      {(extraNumber && extraNumber !== " ") ? (
        <Box sx={{...regroupingNumberStyles, left: "-5px"}}>
          <Text fontWeight={900}>{extraNumber  === "↑" ? "+1" : extraNumber}</Text>
        </Box>
      ) : null}
    </Box>
  );
}
